import {
  registerCellType,
  NumericCellType,
  DropdownCellType,
  DateCellType,
  CheckboxCellType,
  TextCellType,
} from 'handsontable-v16/cellTypes';

import {
  registerPlugin,
  CopyPaste,
  ContextMenu,
  AutoColumnSize,
  Filters,
  Autofill,
  DropdownMenu,
  HiddenRows,
  HiddenColumns,
} from 'handsontable-v16/plugins';

// Custom editors v16
import { ColorEditorV16 } from '@/components/UI/SpreadSheet/editors-v16/ColorEditorV16';
import { ExponentialEditorV16 } from '@/components/UI/SpreadSheet/editors-v16/ExponentialEditorV16';
import { MultiSelectEditorV16 } from '@/components/UI/SpreadSheet/editors-v16/MultiSelectEditorV16';
import { DateEditorWrapperV16 } from '@/components/UI/SpreadSheet/editors-v16/DateEditorWrapperV16';
import { DateTimeEditorV16 } from '@/components/UI/SpreadSheet/editors-v16/DateTimeEditorV16';
import { ScoringScaleEditorV16 } from '@/components/UI/SpreadSheet/editors-v16/ScoringScaleEditorV16';
import { TimepointEditorV16 } from '@/components/UI/SpreadSheet/editors-v16/TimepointEditorV16';

// Custom validators v16
import { registerCustomValidatorsV16 } from './hot-v16.validators';

// Custom renderers v16
import { registerCustomRenderersV16 } from './hot-v16.renderers';

import Handsontable from 'handsontable-v16';
import type {
  MetadataField,
  MetadataFieldWithValue,
  MetadataFieldValueTypes,
  MetadataFieldTypes,
} from '@/model/Metadata.model';
import { DateInputUtils, DateUtils } from '@/utils/Date.utils';
import { numericValidatorFactory, textValidatorFactory, createHotRequiredValidator } from '@/validators';
import { formatNumberComingIn } from '@/helpers';
import { _notNil } from '@/littledash';
import type { ID } from '@/model/Common.model';

let isRegisteredV16 = false;

/**
 * Register all Handsontable v16 modules
 *
 * This function should be called once before using any Handsontable v16 components.
 * It's safe to call multiple times - registration will only happen once.
 */
export const registerHOTModulesV16 = (): void => {
  if (isRegisteredV16) {
    return;
  }

  // Register custom editors
  ColorEditorV16.register();
  ExponentialEditorV16.register();
  MultiSelectEditorV16.register();
  DateEditorWrapperV16.register();
  DateTimeEditorV16.register();
  ScoringScaleEditorV16.register();
  TimepointEditorV16.register();

  // Register cell types
  registerCellType(TextCellType);
  registerCellType(NumericCellType);
  registerCellType(DropdownCellType);
  registerCellType(DateCellType);
  registerCellType(CheckboxCellType);

  // Register plugins
  registerPlugin(CopyPaste);
  registerPlugin(ContextMenu);
  registerPlugin(AutoColumnSize);
  registerPlugin(Filters);
  registerPlugin(Autofill);
  registerPlugin(DropdownMenu);
  registerPlugin(HiddenRows);
  registerPlugin(HiddenColumns);

  // Register custom validators
  registerCustomValidatorsV16(Handsontable);

  // Register custom renderers
  registerCustomRenderersV16(Handsontable);

  isRegisteredV16 = true;
};

/**
 * Reset registration state (useful for testing)
 */
export const resetRegistrationV16 = (): void => {
  isRegisteredV16 = false;
};

/**
 * Check if v16 modules are registered
 */
export const isHOTModulesV16Registered = (): boolean => isRegisteredV16;

// Export row header renderer
export { rowHeaderWithControlsRendererV16 } from './hot-v16.rowHeaderWithControls';

// Export validator and renderer names
export { validatorsV16 } from './hot-v16.validators';
export { renderersV16 } from './hot-v16.renderers';

/**
 * Format metadata title for use as column data accessor
 */
export const formatColumnSettingsDataValueV16 = (str: string): string =>
  encodeURIComponent(str).replace(/[.]+/g, '%2E').toLowerCase();

/**
 * Default metadata to column settings mapper for Handsontable v16
 *
 * Maps metadata fields to Handsontable column settings with appropriate
 * editors, validators, and renderers based on field type.
 *
 * NOTE: Currently uses basic types until v16-compatible custom editors are created
 */
export const defaultMetadataToColumnSettingMapperV16 = (metadata: MetadataField) => {
  const data = `meta.${formatColumnSettingsDataValueV16(metadata.title)}`;
  const metaID = metadata.id;
  const isRequired = metadata.required ?? false;

  switch (metadata.field_type) {
    case 'select': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          metaID,
          data,
          type: MultiSelectEditorV16.type,
          isMultiSelect: false,
          options: metadata.options?.map((option) => unescape(option)),
          width: 150,
          validator: createHotRequiredValidator(
            metadata.title,
            isRequired,
            MultiSelectEditorV16.validatorFactory(metadata.options ?? [], false)
          ),
        },
      };
    }
    case 'multi_select': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          metaID,
          data,
          type: MultiSelectEditorV16.type,
          isMultiSelect: true,
          options: metadata.options,
          width: 150,
          validator: createHotRequiredValidator(
            metadata.title,
            isRequired,
            MultiSelectEditorV16.validatorFactory(metadata.options ?? [], true)
          ),
        },
      };
    }
    case 'text': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          metaID,
          data,
          type: 'text',
          metadataType: 'text',
          width: 150,
          validator: createHotRequiredValidator(metadata.title, isRequired, textValidatorFactory()),
          validationErrorMessage: (value: unknown) => {
            const stringValue = typeof value === 'string' ? value : (value?.toString() ?? '');
            if (stringValue.length > 255) {
              return `Must have a max length of 255 characters`;
            }
            return `Is required`;
          },
        },
      };
    }
    case 'date': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          metaID,
          data,
          type: DateEditorWrapperV16.type,
          dateFormat: DateInputUtils.hotDatePattern,
          width: 150,
          validator: createHotRequiredValidator(metadata.title, isRequired, DateUtils.validatorWithCallback()),
        },
      };
    }
    case 'numeric': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          metaID,
          data,
          type: ExponentialEditorV16.type,
          width: 150,
          validator: createHotRequiredValidator(metadata.title, isRequired, numericValidatorFactory()),
        },
      };
    }
    default: {
      throw new Error(`Unrecognised metadata field_type [${metadata.field_type}]`);
    }
  }
};

/**
 * V16 version of createMeta — converts spreadsheet row metadata into API payload format.
 *
 * Differences from v12 createMeta:
 *  - Editor type names use v16 suffixes (multi-select-v16, numeric-expo-v16)
 *  - Column data keys are already encoded (via formatColumnSettingsDataValueV16) at column definition time
 */
export const createMetaV16 = (
  meta: Record<string, MetadataFieldValueTypes>,
  columns: Array<{ data?: string | number | object; type?: string; metaID?: string | number; isMultiSelect?: boolean }>
): Array<{ value: MetadataFieldValueTypes; glossary_id: string | number }> =>
  Object.entries(meta).reduce<Array<{ value: MetadataFieldValueTypes; glossary_id: string | number }>>(
    (acc, [title, value]) => {
      if (_notNil(title) && _notNil(value) && value !== '') {
        const cellData = columns.find((c) => c.data === `meta.${title}`);
        if (_notNil(cellData)) {
          const { type, metaID, isMultiSelect } = cellData;
          let newValue: MetadataFieldValueTypes;

          if (type === MultiSelectEditorV16.type && typeof value === 'string' && isMultiSelect !== false) {
            newValue = value.split(',');
          } else if (type === ExponentialEditorV16.type) {
            newValue = formatNumberComingIn(value);
          } else {
            newValue = String(value);
          }

          if (_notNil(metaID)) {
            acc.push({ value: newValue, glossary_id: metaID });
          }
        }
      }
      return acc;
    },
    []
  );

/**
 * Check if a metadata value is set (not null, undefined, or empty)
 */
export const valueSetV16 = (value: MetadataFieldValueTypes): boolean => {
  return (
    _notNil(value) &&
    ((typeof value === 'string' && value.trim().length > 0) ||
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      (Array.isArray(value) && value.length > 0))
  );
};

/**
 * Sanitize metadata values based on field type
 */
export const sanitizeMetadataValueV16 = (
  value: MetadataFieldValueTypes,
  type: MetadataFieldTypes
): MetadataFieldValueTypes => {
  if (Array.isArray(value)) {
    return value.filter(valueSetV16);
  }
  if (type === 'numeric' && _notNil(value)) {
    return !isNaN(formatNumberComingIn(value)) ? value : '';
  }

  return value;
};

/**
 * Sorts a metadata map [[glossaryId]: Metadata] to Array<Metadata>
 *
 * @description
 * - Additional metadata added will be placed at the end
 * - Metadata values will be sanitized (ie nil / empty values removed)
 *
 * @param metadataGlossaryIdOrder Array of glossary IDs in desired order
 * @param metadataIdsToRetain Set of metadata IDs to include in output
 * @param metadata Record of metadata by glossary ID
 * @returns Sorted array of metadata with values
 */
export const metadataMapToSortedMetadataArrayV16 = (
  metadataGlossaryIdOrder: Array<number>,
  metadataIdsToRetain: Set<ID>,
  metadata?: Record<string, MetadataFieldWithValue>
): Array<MetadataFieldWithValue> => {
  const md = { ...metadata };
  const metadataArray = metadataGlossaryIdOrder.reduce<Array<MetadataFieldWithValue>>((acc, glossaryId) => {
    if (_notNil(md?.[glossaryId])) {
      const metadataItem = md[glossaryId];
      delete md[glossaryId];
      return [...acc, metadataItem];
    }
    return acc;
  }, []);
  metadataArray.push(...Object.values(md)); // add unsorted metadata to the end
  return metadataArray.reduce<Array<MetadataFieldWithValue>>((acc, metadataItem) => {
    delete metadataItem['options'];
    if (
      metadataIdsToRetain.has(metadataItem.glossary_id) &&
      valueSetV16(sanitizeMetadataValueV16(metadataItem.value, metadataItem.field_type))
    ) {
      return [
        ...acc,
        {
          ...metadataItem,
          value: sanitizeMetadataValueV16(metadataItem.value, metadataItem.field_type),
        },
      ];
    }
    return acc;
  }, []);
};

/**
 * Re-export Handsontable v16 for convenience
 */
export { Handsontable as HandsontableV16 };
